import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';

export type VisibilityMap = Record<string, boolean>;

export type FieldsByInvariantId = Map<string, IFormFieldV2>;

export function buildFieldsByInvariantId(
  formMeta: IFormFieldV2[]
): FieldsByInvariantId {
  const map: FieldsByInvariantId = new Map();
  formMeta.forEach(field => {
    if (field.invariantId) {
      map.set(field.invariantId, field);
    }
  });
  return map;
}

/**
 * Returns the names of the fields whose values control this field's
 * visibility (the targets of its visibility rules).
 */
export function getControllingFieldNames(
  field: IFormFieldV2,
  fieldsByInvariantId: FieldsByInvariantId
): string[] {
  const rules = field.visibility?.rules;
  if (!rules || rules.length === 0) {
    return [];
  }

  const names: string[] = [];
  rules.forEach(rule => {
    const target = rule.targetFieldInvariantId
      ? fieldsByInvariantId.get(rule.targetFieldInvariantId)
      : undefined;
    if (target && !names.includes(target.name)) {
      names.push(target.name);
    }
  });
  return names;
}

/**
 * Evaluates a single field's visibility against the current form values.
 *
 * Semantics (kept identical to the legacy useFormVisibility hook):
 * - no rules: visible unless isDefaultHidden
 * - rules are checked in order; the first rule whose target field value
 *   equals the rule value (case-insensitive string comparison) wins
 * - rules whose target value is null/undefined are skipped
 */
export function evaluateFieldVisibility(
  field: IFormFieldV2,
  fieldsByInvariantId: FieldsByInvariantId,
  values: IFormDataV2
): boolean {
  const defaultVisibility = !field.visibility?.isDefaultHidden;
  const rules = field.visibility?.rules;
  if (!rules || rules.length === 0) {
    return defaultVisibility;
  }

  for (const rule of rules) {
    const target = rule.targetFieldInvariantId
      ? fieldsByInvariantId.get(rule.targetFieldInvariantId)
      : undefined;
    if (!target) continue;

    const value = values[target.name];
    if (value === undefined || value === null) continue;

    const ruleValue = rule.fieldValue?.toLowerCase();
    // A multi-select array holds {id, label} options and matches when it
    // CONTAINS the rule value.
    const matches = Array.isArray(value)
      ? value.some(item => String(item?.id ?? item).toLowerCase() === ruleValue)
      : value.toString().toLowerCase() === ruleValue;
    if (matches) {
      return rule.visibility ?? defaultVisibility;
    }
  }

  return defaultVisibility;
}

export function evaluateVisibilityMap(
  formMeta: IFormFieldV2[],
  values: IFormDataV2,
  fieldsByInvariantId: FieldsByInvariantId = buildFieldsByInvariantId(formMeta)
): VisibilityMap {
  const visibility: VisibilityMap = {};
  formMeta.forEach(field => {
    visibility[field.name] = evaluateFieldVisibility(
      field,
      fieldsByInvariantId,
      values
    );
  });
  return visibility;
}
